import { Box, IconButton, Button, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const NextOfKeen = () => {
  const [nextOfKeens, setNextOfKeens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNextOfKeen, setSelectedNextOfKeen] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [nextOfKeenToDelete, setNextOfKeenToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    cell: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'next_of_keen_V2'); // Fetch next of keen data
        if (response.success) {
          setNextOfKeens(Array.isArray(response.data) ? response.data : []);
        } else {
          toast.error(response.message || "Failed to load next of keen data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch next of keen data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  // Export to Excel function
  const exportToExcel = () => {
    if (nextOfKeens.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // Create CSV content
      const headers = [
        'ID',
        'Full Name',
        'Email',
        'Cell'
      ];

      const csvContent = nextOfKeens.map(item => [
        item.id,
        `"${item.fullName || ''}"`,
        `"${item.email || ''}"`,
        `"${item.cell || ''}"`
      ].join(','));

      // Combine headers and data
      const csv = [headers.join(','), ...csvContent].join('\n');

      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `next_of_keen_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Next of keen data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  const handleAddClick = () => {
    setFormData({
      fullName: '',
      email: '',
      cell: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (nextOfKeen) => {
    setSelectedNextOfKeen(nextOfKeen);
    setFormData({
      fullName: nextOfKeen.fullName || '',
      email: nextOfKeen.email || '',
      cell: nextOfKeen.cell || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (nextOfKeen) => {
    setNextOfKeenToDelete(nextOfKeen);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'next_of_keen_V2', nextOfKeenToDelete.id); // Delete next of keen by ID
      if (response.success) {
        toast.success(`Next of keen deleted successfully`);
        setNextOfKeens(nextOfKeens.filter(item => item.id !== nextOfKeenToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete next of keen");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete next of keen");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setNextOfKeenToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedNextOfKeen) {
        // Update existing next of keen
        response = await updateData('proccess.php', 'next_of_keen_V2', formData, selectedNextOfKeen.id); // Update next of keen
        if (response.success) {
          toast.success(`Next of keen updated successfully`);
          setNextOfKeens( response.data);
        }
      } else {
        // Add new next of keen
        response = await addData('proccess.php', 'next_of_keen_V2', formData); // Add next of keen
        if (response.success) {
          toast.success(`Next of keen added successfully`);
          setNextOfKeens( response.data); // Assuming response.data contains the new next of keen
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedNextOfKeen(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "fullName", headerName: "Full Name", flex: 1, minWidth: 120 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 120 },
    { field: "cell", headerName: "Cell", flex: 1, minWidth: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No next of keen records available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedNextOfKeen(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedNextOfKeen ? "Edit Next of Keen" : "Add New Next of Keen"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                style={{ width: "100%" }}
                id="fullName"
                name="fullName"
                label="Full Name"
                variant="standard"
                value={formData.fullName}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="email"
                name="email"
                label="Email"
                type="email"
                variant="standard"
                value={formData.email}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="cell"
                name="cell"
                label="Cell"
                variant="standard"
                value={formData.cell}
                onChange={handleFormChange}
                required
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedNextOfKeen(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedNextOfKeen ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{nextOfKeenToDelete?.fullName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Next of Keen Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage next of keen records
          </Typography>
        </Box>
        <Box display="flex" gap="1rem">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              borderColor: colors.green[400],
              color: colors.green[400],
              '&:hover': {
                backgroundColor: colors.green[50],
                borderColor: colors.green[500],
              }
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Next of Keen
          </Button>
        </Box>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "45vh", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={nextOfKeens}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default NextOfKeen;